import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { DeviceStatusType, DeviceStatusHistoryType } from '@portals/types';

import { TimelineRangeNamesType } from '../types/device-status-incident-timeline-widget.types';

dayjs.extend(utc);

function generateTimestamps({
  interval,
  ticks,
}: {
  interval: number;
  ticks: number;
}) {
  const startTime = dayjs().utc();

  return Array.from({ length: ticks }, (_, i) => {
    return (
      startTime
        .subtract(i * interval, 'milliseconds')
        .format('YYYY-MM-DD HH:mm:ss') + ' UTC'
    );
  });
}

const INTERVALS: Record<TimelineRangeNamesType, number> = {
  oneHour: 600000,
  sixHours: 3600000,
  oneDay: 14400000,
  sevenDays: 86400000,
};

const STATUS_CYCLE: DeviceStatusType[] = [
  'online',
  'error',
  'offline',
  'unavailable',
];

let previousStatus: DeviceStatusType | null = null;

function getRandomStatus() {
  const availableStatuses = STATUS_CYCLE.filter(
    (status) => status !== previousStatus
  );

  const status =
    availableStatuses[Math.floor(Math.random() * availableStatuses.length)];
  previousStatus = status;

  return status;
}

const TICKS: Record<TimelineRangeNamesType, number> = {
  oneHour: 6,
  sixHours: 6,
  oneDay: 6,
  sevenDays: 7,
};

export function generateDeviceStatusData(
  requestedTimeRange: TimelineRangeNamesType
) {
  const interval = INTERVALS[requestedTimeRange];
  const ticks = TICKS[requestedTimeRange];
  const timestamps = generateTimestamps({
    ticks,
    interval,
  });

  const sortedTimestamps = timestamps.sort((timestampA, timestampB) => {
    const timeA = dayjs(timestampA);
    const timeB = dayjs(timestampB);
    return timeA.isBefore(timeB) ? -1 : 1;
  });

  const events: DeviceStatusHistoryType[] = [];

  sortedTimestamps.forEach((start, index) => {
    const end = sortedTimestamps[index + 1];

    if (!end) {
      return;
    }

    const status = getRandomStatus();

    events.push({
      start,
      end,
      status,
      meta: {
        actual_start: start,
        actual_end: end,
      },
    });
  });

  return events;
}

export function getDeviceStatusColor(status: DeviceStatusType | undefined) {
  switch (status) {
    case 'online':
      return {
        id: 'online',
        displayName: 'Online',
        baseColor: 'teal',
        bg: 'teal.0',
        indicator: 'teal_accent.4',
        borderColor: 'teal.0',
        darkIndicator: 'teal_accent.3',
      };
    case 'offline':
      return {
        id: 'offline',
        displayName: 'Offline',
        baseColor: 'gray',
        bg: 'gray.1',
        indicator: 'gray.4',
        borderColor: 'gray.1',
        darkIndicator: 'gray.4',
      };
    case 'error':
      return {
        id: 'error',
        displayName: 'Error',
        baseColor: 'red',
        bg: 'red.0',
        indicator: 'red.4',
        borderColor: 'red.0',
        darkIndicator: 'red_accent.2',
      };
    case 'unavailable':
      return {
        id: 'unavailable',
        displayName: 'Unavailable',
        baseColor: 'amber',
        bg: 'amber.0',
        indicator: 'amber.7',
        borderColor: 'amber.0',
        darkIndicator: 'amber_accent.4',
      };
    default:
      return {
        id: 'unknown',
        displayName: 'Unknown',
        baseColor: 'white',
        bg: 'white',
        indicator: 'white',
        borderColor: 'gray.2',
        darkIndicator: 'white',
      };
  }
}
